import React, { useState } from 'react';
import { OverlayInjectedProps } from 'react-bootstrap/esm/Overlay';
import { Popover } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';

import './FiltersPopover.scss';
import { DeviceInfoService, DevicesService } from 'services';
import { FilterType, TestsStatusFilter } from 'enums';
import { IDeviceModel, IFirmware, IManufacturer } from 'interfaces';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

// Bootstrap's overlay injects some props into this component.
// However, it adds an "any" to the props, which remove type safety, so
// we'll do some typescript magic to remove that any.
// See: https://stackoverflow.com/questions/51465182/typescript-remove-index-signature-using-mapped-types/51956054
type OverlayPropsNoAny = Pick<OverlayInjectedProps, KnownKeys<OverlayInjectedProps>>

interface IFiltersPopoverProps {
    className?: string;
    id: string;
    onAddFilters: () => void;
}

export const FiltersPopover = React.forwardRef<HTMLDivElement, IFiltersPopoverProps>(
    (props: IFiltersPopoverProps & OverlayPropsNoAny, ref) => {
        let { className, onAddFilters, ...restProps } = props;
        const { t, i18n } = useTranslation();
        const filters = DevicesService.useFilters();
        const [location, setLocation] = useState('');

        if (!className) className = '';
        className += ' filtersPopover';

        // Compute option groups for models and firmwares based on the manufacturers we're filtering by.
        let manufacturers: IManufacturer[] = [];
        let modelGroups: {label: string, key: number, models: IDeviceModel[]}[] = [];
        let firmwareGroups: {label: string, key: number, firmwares: IFirmware[]}[] = [];
        let testsStatus = TestsStatusFilter.All;

        for (const f of filters) {
            if (f.type === FilterType.Manufacturer) {
                manufacturers.push(DeviceInfoService.manufacturersById[f.value]);
            } else if (f.type === FilterType.TestsStatus) {
                testsStatus = f.value;
            }
        }

        // If we don't have any manufacturer filters, default to all.
        if (manufacturers.length === 0) manufacturers = DeviceInfoService.manufacturers;

        for (const man of manufacturers) {
            modelGroups.push({
                label: man.name,
                key: man.id,
                models: man.models,
            });
            firmwareGroups.push({
                label: man.name,
                key: man.id,
                firmwares: man.firmwares,
            });
        }

        const onChangeManufacturer = (e: React.ChangeEvent<HTMLSelectElement>) => {
            DevicesService.addManufacturerFilter(parseInt(e.target.value, 10));
            onAddFilters();
        }
        const onChangeModel = (e: React.ChangeEvent<HTMLSelectElement>) => {
            DevicesService.addModelFilter(parseInt(e.target.value, 10));
            onAddFilters();
        }
        const onChangeFirmware = (e: React.ChangeEvent<HTMLSelectElement>) => {
            DevicesService.addFirmwareFilter(parseInt(e.target.value, 10));
            onAddFilters();
        }
        const onChangeLocation = (e: React.ChangeEvent<HTMLInputElement>) => {
            setLocation(e.target.value);
            // Don't call "onAddFilters" here because the location doesn't get added until
            // the user presses the button in the input.
        }
        const onChangeTestsStatus = (e: React.ChangeEvent<HTMLSelectElement>) => {
            DevicesService.setTestsStatusFilter(e.target.value as TestsStatusFilter);
            onAddFilters();
        }
        const onKeypressLocation = (e: React.KeyboardEvent) => {
            if (e.key === 'Enter') {
                onSubmitLocation();
            }
        }
        const onSubmitLocation = () => {
            const trimmedLocation = location.trim();
            // Only do an add if we have something other than whitespace
            if (trimmedLocation) {
                DevicesService.addLocationFilter(location);
                onAddFilters();
            }
            setLocation('');
        }

        return (
            <Popover ref={ref} className={className} {...restProps}>
                <Popover.Content>
                    <div className="filtersPopover_group">
                        <div className="filtersPopover_label">
                            {t('devicesList.filters.manufacturer')}
                        </div>
                        <div className="filtersPopover_value">
                            {/* We're setting the value to something that doesn't exist so the select will never show a selected value. */}
                            {/* This is because we immediately add the filter when the user selects it. */}
                            <select onChange={onChangeManufacturer} value={-1}>
                                <option value=""></option>
                                {DeviceInfoService.manufacturers.map(m => (
                                    <option value={m.id} key={m.id}>{m.name}</option>
                                ))}
                            </select>
                        </div>
                    </div>
                    <div className="filtersPopover_group">
                        <div className="filtersPopover_label">
                            {t('devicesList.filters.model')}
                        </div>
                        <div className="filtersPopover_value">
                            <select onChange={onChangeModel} value={-1}>
                                <option value=""></option>
                                {modelGroups.map(group => (
                                    <optgroup key={group.key} label={group.label}>
                                        {group.models.map(m => (
                                            <option value={m.id} key={m.id}>{m.name}</option>
                                        ))}
                                    </optgroup>
                                ))}
                            </select>
                        </div>
                    </div>
                    <div className="filtersPopover_group">
                        <div className="filtersPopover_label">
                            {t('devicesList.filters.firmware')}
                        </div>
                        <div className="filtersPopover_value">
                            <select onChange={onChangeFirmware} value={-1}>
                                <option value=""></option>
                                {firmwareGroups.map(group => (
                                    <optgroup key={group.key} label={group.label}>
                                        {group.firmwares.map(f => (
                                            <option value={f.id} key={f.id}>{f.firmware_version}</option>
                                        ))}
                                    </optgroup>
                                ))}
                            </select>
                        </div>
                    </div>
                    <div className="filtersPopover_group">
                        <div className="filtersPopover_label">
                            {t('devicesList.filters.location')}
                        </div>
                        <div className="filtersPopover_value">
                            <div className="inputWithButton">
                                <input
                                    type="text"
                                    value={location}
                                    onChange={onChangeLocation}
                                    onKeyPress={onKeypressLocation}
                                    maxLength={128}
                                />
                                <button type="button" onClick={onSubmitLocation}>
                                    <FontAwesomeIcon icon="angle-right" />
                                </button>
                            </div>
                        </div>
                    </div>
                    <div className="filtersPopover_group">
                        <div className="filtersPopover_label">
                            {t('devicesList.filters.testsStatus')}
                        </div>
                        <div className="filtersPopover_value">
                            <select onChange={onChangeTestsStatus} value={testsStatus}>
                                <option value={TestsStatusFilter.All}></option>
                                <option value={TestsStatusFilter.HasFailing}>
                                    {t('devicesList.filters.testsStatusHasFailing')}
                                </option>
                                <option value={TestsStatusFilter.AllPass}>
                                    {t('devicesList.filters.testsStatusAllPass')}
                                </option>
                                <option value={TestsStatusFilter.HasUnrun}>
                                    {t('devicesList.filters.testsStatusHasUnrun')}
                                </option>
                            </select>
                        </div>
                    </div>
                </Popover.Content>
            </Popover>
        )
    }
);